import React from 'react';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { analyticsService } from '../../services';
import { RangeValue } from '../../constants/product.constant';
import StatCard from '../../components/analytics/StatCard';
import ChartCard from '../../components/analytics/ChartCard';
import { Loadable, useAnalyticsData } from '../../components/analytics/tabHelpers';
import { fmtNum, CHART_COLORS } from '../../components/analytics/format';

const useStyles = makeStyles(() => ({
  section: { fontSize: 16, fontWeight: 700, color: '#1f2937', margin: '8px 4px 0' },
}));

const SuperCoachTab: React.FC<{ days: RangeValue }> = ({ days }) => {
  const classes = useStyles();
  const { loading, error, data } = useAnalyticsData<any>(() => analyticsService.getSuperCoach(days), [days]);

  const profiles = data?.profiles || {};
  const bookings = data?.bookings || {};
  const byStatus = (bookings?.byStatus || []).map((s: any) => ({ status: s._id || '(none)', count: s.count }));
  const freeVsPaid = (bookings?.freeVsPaid || []).map((s: any) => ({
    name: s._id ? 'Free' : 'Paid', value: s.count,
  }));

  return (
    <Loadable loading={loading} error={error}>
      <Grid container spacing={3}>
        <Grid item xs={12}><Typography className={classes.section}>Profiles</Typography></Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard label="Parents" value={fmtNum(profiles.parents)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard label="Tutors (real)" value={fmtNum(profiles.tutors)} sub="Excludes mocked" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard label="Children" value={fmtNum(profiles.children)} />
        </Grid>

        <Grid item xs={12}><Typography className={classes.section}>Bookings (window)</Typography></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Total bookings" value={fmtNum(bookings.total)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Paid payments" value={fmtNum(bookings.paidPayments)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Distinct parents" value={fmtNum(bookings.distinctParents)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Distinct tutors" value={fmtNum(bookings.distinctTutors)} />
        </Grid>

        <Grid item xs={12} md={8}>
          <ChartCard title="Bookings by status">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byStatus} margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Free vs paid">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={freeVsPaid} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {freeVsPaid.map((_: any, i: number) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Loadable>
  );
};

export default SuperCoachTab;
